import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  FilePlus,
  FolderPlus,
  GripVertical,
  PanelLeft,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import type {
  DocFolderWithAccess,
  DocPage,
  WriteRejection,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { FeedbackButton } from "@/components/feedback/FeedbackButton";
import { EmptyState, LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { FolderTree } from "@/modules/docs/screens/FolderTree";
import {
  MoveFolderDialog,
  type MoveFolderTarget,
} from "@/modules/docs/screens/MoveFolderDialog";
import { MoveToFolderDialog } from "@/modules/docs/screens/MoveToFolderDialog";
import { NamePromptDialog } from "@/modules/docs/screens/NamePromptDialog";
import { PageReader } from "@/modules/docs/screens/PageReader";
import {
  filterPagesByTitle,
  firstPageId,
  folderTrail,
  foldersToShow,
  recentPages,
} from "@/modules/docs/tree";
import { useDocsTree } from "@/modules/docs/useDocsTree";
import { useResizableTree } from "@/modules/docs/useResizableTree";
import { useOpenRecord } from "@/routes/useOpenRecord";
import { describeRejection } from "@/modules/docs/writeRejection";

/**
 * The folder tree and the reader (D1-1, D1-2; redesigned 2026-09-27, §7.8):
 * a tree of folders on the left, built from nothing but what `listFolders`
 * returned, and the selected page's content, editor and versions on the
 * right.
 *
 * This section declares no fields (see `index.ts`) — it is a tree and a
 * reader, not a filterable list, the same reason `ContactTypes` declares
 * none.
 */
export function Browse() {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const treeQuery = useDocsTree();
  const pageOpen = useOpenRecord();
  const isMobile = useIsMobile();
  const permissions = usePermissions();
  // Resizable and collapsible (#403, the owner's own feedback on §7.8). Phone
  // width is untouched — the tree stays behind the Documents sheet there, and
  // this hook's state is only ever read by the desktop branch below.
  const resizableTree = useResizableTree();

  // Which folder "+ New document" and "+ New folder" write into. Set by
  // clicking a folder row directly, or by opening a page (its own folder
  // becomes the context) — not URL state, unlike the open page: this is
  // where the *next* write goes, not something a shared link should pin.
  const [clickedFolderId, setClickedFolderId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [mobileTreeOpen, setMobileTreeOpen] = useState(false);

  // The page a create-document action just opened, so it can mount into edit
  // mode once (§7.8: "opens straight into edit mode with an empty body").
  // Retired by `PageReader` calling `onEditSessionEnd` the moment that first
  // edit ends (Save or Cancel) — from that event, not an effect watching for
  // it, so re-opening the same page later does not send it back into edit
  // mode.
  const [justCreatedPageId, setJustCreatedPageId] = useState<string | null>(
    null,
  );

  const [createDocOpen, setCreateDocOpen] = useState(false);
  const [createDocName, setCreateDocName] = useState("");
  const [createDocRejection, setCreateDocRejection] =
    useState<WriteRejection | null>(null);

  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [createFolderName, setCreateFolderName] = useState("");
  const [createFolderRejection, setCreateFolderRejection] =
    useState<WriteRejection | null>(null);

  const [renamingFolder, setRenamingFolder] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [renameFolderName, setRenameFolderName] = useState("");
  const [renameFolderRejection, setRenameFolderRejection] =
    useState<WriteRejection | null>(null);

  // "Move to…", from a page's "⋯" menu (FR-DOC-10). Which page is moving is
  // its own state, distinct from `clickedFolderId` — moving a page never
  // changes what "+ New document" writes into.
  const [movingPage, setMovingPage] = useState<DocPage | null>(null);
  const [moveTargetFolderId, setMoveTargetFolderId] = useState<string | null>(
    null,
  );
  const [moveRejection, setMoveRejection] = useState<WriteRejection | null>(
    null,
  );

  // "Move to…", from a folder's own "⋯" menu (FR-DOC-10, #414) — the same
  // shape as the page move above, kept as its own state since a folder and
  // a page can each be mid-move at once (they cannot in practice, since
  // opening one dialog does not close the other's trigger, but nothing here
  // assumes only one story of the two is ever true).
  const [movingFolder, setMovingFolder] = useState<DocFolderWithAccess | null>(
    null,
  );
  const [moveFolderTarget, setMoveFolderTarget] = useState<MoveFolderTarget>({
    kind: "none",
  });
  const [moveFolderRejection, setMoveFolderRejection] =
    useState<WriteRejection | null>(null);

  function invalidateTree() {
    void queryClient.invalidateQueries({ queryKey: ["docs"] });
  }

  const createDocMutation = useMutation({
    mutationFn: (input: { folderId: string; title: string }) =>
      data.docs.createPage(input.folderId, input.title),
    onSuccess: (result) => {
      if (result.ok) {
        setJustCreatedPageId(result.data.id);
        pageOpen.open(result.data.id);
        setCreateDocOpen(false);
        setCreateDocName("");
        setCreateDocRejection(null);
        setMobileTreeOpen(false);
        invalidateTree();
      } else {
        setCreateDocRejection(result.rejection);
      }
    },
  });

  const createFolderMutation = useMutation({
    mutationFn: (input: { parentId: string | null; name: string }) =>
      data.docs.createFolder(input.parentId, input.name),
    onSuccess: (result) => {
      if (result.ok) {
        setClickedFolderId(result.data.id);
        pageOpen.close();
        setCreateFolderOpen(false);
        setCreateFolderName("");
        setCreateFolderRejection(null);
        setMobileTreeOpen(false);
        invalidateTree();
      } else {
        setCreateFolderRejection(result.rejection);
      }
    },
  });

  const renameFolderMutation = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      data.docs.renameFolder(input.id, input.name),
    onSuccess: (result) => {
      if (result.ok) {
        setRenamingFolder(null);
        setRenameFolderRejection(null);
        invalidateTree();
      } else {
        setRenameFolderRejection(result.rejection);
      }
    },
  });

  /**
   * Soft delete, from the "⋯" menu, after a confirm dialog (FR-DOC-13). The
   * dialog is `window.confirm` rather than a component of its own — the same
   * native confirm `PageReader` already uses for "leave without saving", and
   * the only destructive-confirm pattern this codebase has needed so far.
   */
  const deletePageMutation = useMutation({
    mutationFn: (page: DocPage) => data.docs.deletePage(page.id),
    onSuccess: (result, page) => {
      if (result.ok) {
        // The deleted page just left the tree — showing it open would be
        // showing a page that is no longer there to show.
        if (pageOpen.recordId === page.id) pageOpen.close();
        invalidateTree();
      } else {
        // The menu item is already hidden without Manage on the folder
        // (`FolderTree`'s own courtesy) — reaching here at all means
        // something changed underneath the click, not a routine refusal.
        window.alert(describeRejection(result.rejection));
      }
    },
  });

  function requestDeletePage(page: DocPage) {
    if (
      window.confirm(
        `Delete "${page.title}"? It can be restored later from this folder's Deleted view.`,
      )
    ) {
      deletePageMutation.mutate(page);
    }
  }

  const movePageMutation = useMutation({
    mutationFn: (input: { pageId: string; folderId: string }) =>
      data.docs.movePage(input.pageId, input.folderId),
    onSuccess: (result) => {
      if (result.ok) {
        setMovingPage(null);
        setMoveTargetFolderId(null);
        setMoveRejection(null);
        invalidateTree();
      } else {
        setMoveRejection(result.rejection);
      }
    },
  });

  const moveFolderMutation = useMutation({
    mutationFn: (input: { folderId: string; parentId: string | null }) =>
      data.docs.moveFolder(input.folderId, input.parentId),
    onSuccess: (result) => {
      if (result.ok) {
        setMovingFolder(null);
        setMoveFolderTarget({ kind: "none" });
        setMoveFolderRejection(null);
        invalidateTree();
      } else {
        setMoveFolderRejection(result.rejection);
      }
    },
  });

  if (treeQuery.isPending) return <TableSkeleton rows={6} />;

  if (treeQuery.isError) {
    return (
      <LoadFailed
        message={treeQuery.error.message}
        onRetry={() => {
          void treeQuery.refetch();
        }}
      />
    );
  }

  const { folders, pagesByFolder, contentByPageId } = treeQuery.data;

  // Falls back to the first page in path order whenever nothing is picked
  // yet, or whatever was picked no longer resolves — switching folders
  // included, since the old selection is rarely a page in the new one.
  // Derived on every render rather than reset from an effect.
  const requestedPageId = pageOpen.recordId;
  const requestedPageValid =
    requestedPageId !== null && contentByPageId.has(requestedPageId);
  const clickedFolder = clickedFolderId
    ? folders.find((folder) => folder.id === clickedFolderId)
    : undefined;
  // A folder was clicked directly and no page is open — browsing it, with
  // nothing chosen inside it yet, is the point, not a state to fall back
  // out of into whatever page happened to load first.
  const showingFolder = !requestedPageValid && clickedFolder !== undefined;

  const effectivePageId = requestedPageValid
    ? requestedPageId
    : showingFolder
      ? null
      : firstPageId(folders, pagesByFolder);

  const page = effectivePageId
    ? contentByPageId.get(effectivePageId)
    : undefined;
  const owningFolder = page
    ? folders.find((folder) => folder.id === page.folderId)
    : undefined;

  const rootFolders = folders
    .filter((folder) => folder.parentId === null)
    .sort((a, b) => a.path.localeCompare(b.path));
  const targetFolderId =
    page?.folderId ?? clickedFolder?.id ?? rootFolders[0]?.id ?? null;
  const targetFolder = targetFolderId
    ? folders.find((folder) => folder.id === targetFolderId)
    : undefined;

  const trimmedSearch = searchTerm.trim();
  const filteredPagesByFolder =
    trimmedSearch === ""
      ? pagesByFolder
      : filterPagesByTitle(pagesByFolder, trimmedSearch);
  const filteredFolders =
    trimmedSearch === ""
      ? folders
      : foldersToShow(folders, filteredPagesByFolder);
  const treeEmptyMessage =
    trimmedSearch === ""
      ? "No folders."
      : `No documents match "${trimmedSearch}".`;

  function openCreateDoc() {
    setCreateDocRejection(null);
    setCreateDocName("");
    setCreateDocOpen(true);
  }

  function openCreateFolder() {
    setCreateFolderRejection(null);
    setCreateFolderName("");
    setCreateFolderOpen(true);
  }

  function selectPage(selected: DocPage) {
    pageOpen.open(selected.id);
    setClickedFolderId(null);
    setMobileTreeOpen(false);
  }

  function selectFolder(folderId: string) {
    setClickedFolderId(folderId);
    pageOpen.close();
    setMobileTreeOpen(false);
  }

  const recent = recentPages(treeQuery.data.allPages, contentByPageId, 5);
  const canSeeTrash = can(permissions, "docs.page.delete");

  /*
    A quiet tree (#641): one search, one "+", Recent first, the folders, and
    Trash at the bottom. Recent, Share and Trash used to be sidebar entries;
    their routes stay, reached from here.
  */
  const treePane = (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <Input
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
          }}
          placeholder="Search titles"
          aria-label="Search document titles"
          className="h-8"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              size="icon-sm"
              aria-label="New page or folder"
              className="shrink-0"
            >
              <Plus />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              disabled={!targetFolderId}
              onSelect={openCreateDoc}
            >
              <FilePlus /> New page
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={openCreateFolder}>
              <FolderPlus /> New folder
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto">
        {trimmedSearch === "" && recent.length > 0 ? (
          <section aria-labelledby="docs-recent" className="flex flex-col">
            <div className="flex items-center justify-between px-1 pb-1">
              <h3
                id="docs-recent"
                className="text-muted-foreground text-[11px] font-semibold tracking-wider uppercase"
              >
                Recent
              </h3>
              <Link
                to="/docs/recent"
                className="text-muted-foreground hover:text-foreground text-xs"
              >
                All
              </Link>
            </div>
            <ul className="space-y-0.5">
              {recent.map((recentPage) => (
                <li key={recentPage.id}>
                  <button
                    type="button"
                    onClick={() => {
                      selectPage(recentPage);
                    }}
                    className={cn(
                      "hover:bg-muted/50 w-full truncate rounded-md px-2 py-1 text-left text-sm",
                      effectivePageId === recentPage.id &&
                        "bg-accent font-medium",
                    )}
                  >
                    {recentPage.title}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        <section aria-labelledby="docs-folders" className="flex flex-col">
          {trimmedSearch === "" ? (
            <h3
              id="docs-folders"
              className="text-muted-foreground px-1 pb-1 text-[11px] font-semibold tracking-wider uppercase"
            >
              Folders
            </h3>
          ) : null}
          <FolderTree
            folders={filteredFolders}
            pagesByFolder={filteredPagesByFolder}
            selectedFolderId={clickedFolder?.id ?? null}
            onSelectFolder={selectFolder}
            selectedPageId={effectivePageId}
            onSelectPage={(selected) => {
              selectPage(selected);
            }}
            onRenameFolder={(id, name) => {
              setRenamingFolder({ id, name });
              setRenameFolderName(name);
              setRenameFolderRejection(null);
            }}
            onMoveFolder={(folder) => {
              setMovingFolder(folder);
              setMoveFolderTarget({ kind: "none" });
              setMoveFolderRejection(null);
            }}
            onManageFolderAccess={(id) => {
              void navigate({
                to: "/docs/permissions",
                search: { record: id },
              });
            }}
            onViewDeleted={(id) => {
              void navigate({
                to: "/docs/deleted",
                search: { record: id },
              });
            }}
            onMovePage={(movedPage) => {
              setMovingPage(movedPage);
              setMoveTargetFolderId(null);
              setMoveRejection(null);
            }}
            onDeletePage={requestDeletePage}
            emptyMessage={treeEmptyMessage}
          />
        </section>
      </div>
      {canSeeTrash ? (
        <Link
          to="/docs/deleted"
          className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 border-t px-1 pt-2 text-sm"
        >
          <Trash2 className="size-3.5" aria-hidden />
          Trash
        </Link>
      ) : null}
    </div>
  );

  return (
    /*
      A "Documents" button behind a Sheet on a phone, not the tree stacked
      above the reader (#346's earlier fix, superseded 2026-09-27): a folder
      tree at phone width pushed the page far enough down that "reading it"
      meant scrolling past a tree first. Tucking it behind a button is also
      what "Edit uses the full screen" (§7.8) turns out to mean in practice —
      there is no second pane left competing for the viewport.
    */
    <div className="flex h-full min-h-0 flex-col md:flex-row md:gap-4">
      {isMobile ? (
        <div className="shrink-0 pb-2">
          <Sheet open={mobileTreeOpen} onOpenChange={setMobileTreeOpen}>
            <SheetTrigger asChild>
              <Button type="button" variant="outline" size="sm">
                <PanelLeft />
                Documents
              </Button>
            </SheetTrigger>
            <SheetContent
              title="Documents"
              side="left"
              // The page header's Feedback button is under this sheet's
              // overlay while it is open (#511).
              actions={<FeedbackButton />}
              className="w-[88vw] max-w-sm"
            >
              <div className="flex h-full min-h-0 flex-col p-3">{treePane}</div>
            </SheetContent>
          </Sheet>
        </div>
      ) : resizableTree.collapsed ? (
        // The collapsed rail (#403): a thin strip that is itself the button
        // back — "clicking the rail or the button restores it" is one click
        // target, not two.
        <button
          type="button"
          onClick={resizableTree.toggleCollapsed}
          aria-label="Expand the document tree"
          title="Expand the document tree"
          className="text-muted-foreground hover:bg-muted/50 hover:text-foreground flex w-8 shrink-0 items-center justify-center border-r"
        >
          <PanelLeftOpen className="size-4" aria-hidden />
        </button>
      ) : (
        <div
          className="relative flex min-h-0 shrink-0 flex-col border-r"
          style={{ width: resizableTree.width }}
        >
          <div className="flex items-center justify-end px-1 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Collapse the document tree"
              title="Collapse the document tree"
              onClick={resizableTree.toggleCollapsed}
            >
              <PanelLeftClose className="size-4" />
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto px-2 pb-2">
            {treePane}
          </div>
          {/*
            Dragging this handle resizes the pane, 200–480px, remembered per
            browser (#403). A wide hit target (`w-3`) around a thin visible
            line, the same reason a splitter is never drawn at its true
            1px width — nobody can reliably grab one pixel.
          */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the document tree"
            className="hover:bg-accent active:bg-accent absolute top-0 right-0 flex h-full w-3 -translate-x-1/2 cursor-col-resize touch-none items-center justify-center"
            {...resizableTree.handleProps}
          >
            <GripVertical
              className="text-muted-foreground size-3"
              aria-hidden
            />
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto pb-4">
        {page ? (
          <PageReader
            key={page.id}
            page={page}
            folderPath={
              owningFolder ? folderTrail(folders, owningFolder.id) : ""
            }
            initialMode={justCreatedPageId === page.id ? "edit" : undefined}
            onEditSessionEnd={() => {
              setJustCreatedPageId(null);
            }}
          />
        ) : showingFolder && clickedFolder ? (
          <EmptyState
            title={`Nothing in ${clickedFolder.name} yet`}
            description="No documents have been written here yet."
            action={
              <Button type="button" size="sm" onClick={openCreateDoc}>
                <FilePlus />
                New document
              </Button>
            }
          />
        ) : (
          <EmptyState
            title="No pages yet"
            description="Nothing has been written in this folder yet."
          />
        )}
      </div>

      <NamePromptDialog
        open={createDocOpen}
        onOpenChange={setCreateDocOpen}
        title="New document"
        label="Title"
        placeholder="e.g. Return policy"
        hint={targetFolder ? `Created inside ${targetFolder.path}` : undefined}
        value={createDocName}
        onChangeValue={setCreateDocName}
        pending={createDocMutation.isPending}
        rejection={createDocRejection}
        onSubmit={(title) => {
          if (!targetFolderId) return;
          createDocMutation.mutate({ folderId: targetFolderId, title });
        }}
      />

      <NamePromptDialog
        open={createFolderOpen}
        onOpenChange={setCreateFolderOpen}
        title="New folder"
        label="Name"
        placeholder="e.g. Shipping"
        hint={
          targetFolder
            ? `Created inside ${targetFolder.path}`
            : "Created at the top level"
        }
        value={createFolderName}
        onChangeValue={setCreateFolderName}
        pending={createFolderMutation.isPending}
        rejection={createFolderRejection}
        onSubmit={(name) => {
          createFolderMutation.mutate({ parentId: targetFolderId, name });
        }}
      />

      <NamePromptDialog
        open={renamingFolder !== null}
        onOpenChange={(open) => {
          if (!open) setRenamingFolder(null);
        }}
        title="Rename folder"
        label="Name"
        submitLabel="Rename"
        value={renameFolderName}
        onChangeValue={setRenameFolderName}
        pending={renameFolderMutation.isPending}
        rejection={renameFolderRejection}
        onSubmit={(name) => {
          if (!renamingFolder) return;
          renameFolderMutation.mutate({ id: renamingFolder.id, name });
        }}
      />

      {movingPage ? (
        <MoveToFolderDialog
          open={movingPage !== null}
          onOpenChange={(open) => {
            if (!open) setMovingPage(null);
          }}
          folders={folders}
          currentFolderId={movingPage.folderId}
          selectedFolderId={moveTargetFolderId}
          onChangeSelectedFolderId={setMoveTargetFolderId}
          pending={movePageMutation.isPending}
          rejection={moveRejection}
          onSubmit={(folderId) => {
            movePageMutation.mutate({ pageId: movingPage.id, folderId });
          }}
        />
      ) : null}

      {movingFolder ? (
        <MoveFolderDialog
          open={movingFolder !== null}
          onOpenChange={(open) => {
            if (!open) setMovingFolder(null);
          }}
          folders={folders}
          movingFolder={movingFolder}
          selectedTarget={moveFolderTarget}
          onChangeSelectedTarget={setMoveFolderTarget}
          pending={moveFolderMutation.isPending}
          rejection={moveFolderRejection}
          onSubmit={(parentId) => {
            moveFolderMutation.mutate({
              folderId: movingFolder.id,
              parentId,
            });
          }}
        />
      ) : null}
    </div>
  );
}
